import type { WeeklyZones } from '@painpal/contracts';
import type { ZoneModel } from '@painpal/core';
import type { ZoneSource } from '../../api/keys';
import { AXIS_TEXT, chartTooltip, dataIndexOf } from '../../lib/chartTheme';
import type { BarSeriesOption, ECOption } from '../../lib/echarts';
import { formatHoursMinutes, formatMonthDay } from '../../lib/format';
import { hrModelFor, itemAt, zoneLabels } from '../../lib/zones';
import { colors, ZONE_PALETTES } from '../../theme/tokens';
import type { Variant } from '../../ui/useMediaQuery';
import { categoryLabel } from './fitnessChartOption';

export interface WeekRow { weekStart: string; seconds: number[] }
export interface WeeklyView { rows: WeekRow[]; names: string[]; colors: string[] }

export const WEEKLY_HEIGHT = 240;
export const WEEKLY_LABEL = 'Weekly time in each zone';

export const weekPx = (variant: Variant): number => (variant === 'phone' ? 28 : 14);
const hours = (seconds: number): number => seconds / 3600;
const tenths = (value: number): string => String(Math.round(value * 10) / 10);

// The server already fills empty Monday weeks with zeros, so rows are plotted as returned.
export function weeklyView(data: WeeklyZones, source: ZoneSource): WeeklyView | null {
  if (data.bounds === undefined) return null;
  const count = data.bounds.length + 1;
  const rows = data.weeks;
  if (rows.every((row) => row.seconds.every((s) => s === 0))) return null;
  const model: ZoneModel = source === 'hr' ? hrModelFor(count) : source;
  const names = zoneLabels(model);
  return { rows, names, colors: [...ZONE_PALETTES[model]] };
}

export function weeklyTooltipHtml(row: WeekRow, names: readonly string[]): string {
  const total = row.seconds.reduce((sum, s) => sum + s, 0);
  const lines = names.map((name, z) => `${name} ${formatHoursMinutes(row.seconds[z] ?? 0)}`);
  return [`<b>WEEK OF ${formatMonthDay(row.weekStart)}</b>`, ...lines, `TOTAL ${formatHoursMinutes(total)}`].join('<br/>');
}

export function weeklyTooltip(rows: WeekRow[], names: readonly string[]): (params: unknown) => string {
  return (params) => {
    const row = rows[dataIndexOf(params)];
    return row === undefined ? '' : weeklyTooltipHtml(row, names);
  };
}

export function weeklyZonesOption(view: WeeklyView, variant: Variant = 'desktop'): ECOption {
  const series = view.names.map((name, z): BarSeriesOption => ({
    name, type: 'bar', stack: 'zones', barCategoryGap: '30%',
    data: view.rows.map((row) => hours(row.seconds[z] ?? 0)), itemStyle: { color: itemAt(view.colors, z) },
  }));
  return {
    grid: { left: 36, right: 8, top: 16, bottom: 24 },
    xAxis: {
      type: 'category', data: view.rows.map((row) => row.weekStart), axisTick: { show: false },
      axisLine: { lineStyle: { color: colors.ink } }, axisLabel: { ...AXIS_TEXT, formatter: categoryLabel },
    },
    yAxis: {
      // Tenth-hour labels; a finer interval would print the same label twice.
      type: 'value', min: 0, minInterval: 0.1, splitNumber: 3, name: 'H', nameTextStyle: AXIS_TEXT,
      axisLine: { show: false }, axisTick: { show: false }, splitLine: { lineStyle: { color: colors.line } }, axisLabel: { ...AXIS_TEXT, formatter: tenths },
    },
    tooltip: chartTooltip({ phone: variant === 'phone', formatter: weeklyTooltip(view.rows, view.names), axisPointer: { type: 'shadow' } }),
    series,
  };
}
